import { view } from '@forge/bridge'
import type { History } from 'history'
import posthog from 'posthog-js'
import type React from 'react'
import { useEffect, useState } from 'react'
import {
  BrowserRouter,
  MemoryRouter,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams
} from 'react-router'
import { useSnapshot } from 'valtio'
import { ErrorDisplay, logError } from '@/components/Error/Error'
import { Loading } from '@/components/Loading/Loading'
import { api } from '@/lib/api'
import { getHistory } from '@/lib/history'
import { getRadarData, getRadarsConfig, state, useRadarNav } from '@/lib/state'
import ItemPage from '@/pages/[quadrant]/[id]'
import QuadrantPage from '@/pages/[quadrant]/QuadrantPage'
import AdminBillingPage from '@/pages/Admin/AdminBillingPage'
import AdminEditItemPage from '@/pages/Admin/AdminEditItemPage'
// Admin routes
import AdminItemsPage from '@/pages/Admin/AdminItemsPage'
import AdminNewItemPage from '@/pages/Admin/AdminNewItemPage'
import AdminRadarListPage from '@/pages/Admin/AdminRadarListPage'
import AdminSettingsPage from '@/pages/Admin/AdminSettingsPage'
import CancelPage from '@/pages/cancel'
import HelpAndAboutPage from '@/pages/help-and-about-tech-radar'
import OverviewPage from '@/pages/overview'
// Radar routes
import RadarPage from '@/pages/RadarPage'
import SuccessPage from '@/pages/success'
import type { State } from '../../types'
import { AdminLayout } from './components/Admin/AdminLayout'

// Utility hook to get current path and search
function useCurrentPathAndSearch(isLocal: boolean) {
  const { pathname, search } = useLocation()
  if (isLocal) {
    return {
      pathname: window.location.pathname,
      search: window.location.search
    }
  } else {
    return { pathname, search }
  }
}

export const RadarInitializer = () => {
  const { radarId } = useParams()
  const { currentRadarId } = useSnapshot(state)

  useEffect(() => {
    if (!radarId && !currentRadarId) return

    console.info('RadarInitializer', 'Getting Radar Data', {
      radarId,
      currentRadarId
    })
    getRadarData(Number(radarId || currentRadarId))
  }, [radarId, currentRadarId])

  return <Outlet />
}

// Centralized error route
const ErrorRoute = (
  <Route
    path="*"
    element={<ErrorDisplay error={new Error('Unable to load page.')} />}
  />
)

function AdminRouteSet({ prefix = '' }) {
  return (
    <Route path={`${prefix}/`} element={<AdminLayout />}>
      <Route index element={<AdminRadarListPage />} />
      <Route path="radar-list" element={<AdminRadarListPage />} />
      <Route path="billing" element={<AdminBillingPage />} />
      <Route path="items" element={<AdminItemsPage />} />
      <Route path="settings" element={<AdminSettingsPage />} />
      <Route path="new-item" element={<AdminNewItemPage />} />
      <Route path="edit-item/:itemId" element={<AdminEditItemPage />} />
      {/* Radar-specific admin routes */}
      <Route path="radar/:radarId" element={<AdminItemsPage />} />
      <Route path="radar/:radarId/admin" element={<AdminItemsPage />} />
      <Route path="radar/:radarId/items" element={<AdminItemsPage />} />
      <Route path="radar/:radarId/settings" element={<AdminSettingsPage />} />
      <Route path="radar/:radarId/new-item" element={<AdminNewItemPage />} />
      <Route
        path="radar/:radarId/edit-item/:itemId"
        element={<AdminEditItemPage />}
      />
    </Route>
  )
}

function MainRouteSet() {
  return (
    <Route path="/">
      <Route path="radar/:radarId/" element={<RadarInitializer />}>
        <Route index element={<RadarPage />} />
        <Route path="overview" element={<OverviewPage />} />
        <Route
          path="help-and-about-tech-radar"
          element={<HelpAndAboutPage />}
        />
        <Route path=":quadrantId" element={<QuadrantPage />} />
        <Route path=":quadrantId/:itemId" element={<ItemPage />} />
        {ErrorRoute}
      </Route>
      <Route path="success" element={<SuccessPage />} />
      <Route path="cancel" element={<CancelPage />} />
      {ErrorRoute}
    </Route>
  )
}

const initApp = async () => {
  try {
    // Initialize environment
    const env = await api.getEnv()
    console.info('App', 'Got environment configuration', env)

    // Initialize theme and history
    console.info('App', 'Initializing theme and context')
    view.theme.enable()

    const context = await view.getContext()
    const isLocal = context?.localId === 'local-dev'
    console.info('App', 'Got context', {
      localId: context?.localId,
      moduleKey: context?.moduleKey
    })

    // Disable console logs in production or embed mode
    if (
      context?.environmentType === 'PRODUCTION' ||
      location.search.includes('apiUrl=')
    ) {
      console.log = () => {}
      console.info = () => {}
      console.warn = () => {}
    }

    // Initialize Posthog
    if (env.posthog.api_key) {
      const userEmail = env.userEmail
      posthog.init(env.posthog.api_key, {
        api_host: env.posthog.api_host,
        disable_surveys: true
      })
      if (context?.siteUrl && context.cloudId) {
        const name =
          RegExp(/https:\/\/([^.]+)\.atlassian\.net/).exec(
            context.siteUrl
          )?.[1] || context.siteUrl

        posthog.group('organization', name)

        const userProps: Record<string, string> = {
          name: userEmail || name,
          siteUrl: context.siteUrl,
          cloudId: context.cloudId
        }
        if (userEmail) userProps.email = userEmail
        posthog.identify(context.accountId, userProps)
      }
    }

    const history = await getHistory()
    console.info('App', 'Got Forge history', {
      action: history.action,
      pathname: history?.location?.pathname
    })

    // Update state with results from initApp
    Object.assign(state, {
      context,
      env,
      isLocal,
      history,
      userSettings: env.userSettings || null,
      userEmail: env.userEmail || null
    } as State)
  } catch (error) {
    logError('App', 'Failed to initialize app', error)
  }
  return history as unknown as History
}

export function App() {
  const { error, isAdmin, isLocal } = useSnapshot(state)
  const [initialPath, setInitialPath] = useState<string | null>(null)

  useEffect(() => {
    let cleanup: (() => void) | undefined
    const runInit = async () => {
      // Run initApp and get the history object
      const history = await initApp()
      let path = '/'
      if (isLocal) {
        path = window.location.pathname + window.location.search
      } else if (history?.location) {
        path = history.location.pathname + (history.location.search || '')
      }
      setInitialPath(path)
    }
    runInit()
    return () => {
      if (cleanup) cleanup()
    }
  }, [isLocal])

  useEffect(() => {
    console.info('App', 'Getting radars config')
    getRadarsConfig()
  }, [])

  if (error) {
    return <ErrorDisplay error={error} />
  }

  // Wait for initialPath to be set before rendering router
  if (!initialPath) {
    return <Loading />
  }

  // For admin mode in production (Forge), still use the separate admin routing
  if (isAdmin && !isLocal) {
    console.info('App', 'Rendering AdminApp for production admin mode')
    // Use initialPath for AdminApp
    return <AdminApp initialPath={initialPath} />
  }

  // In development, use BrowserRouter for URL-based routing
  if (isLocal) {
    console.info('App', 'Rendering BrowserRouter for dev mode')
    return (
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    )
  }

  // In production, use MemoryRouter for SPA navigation
  // Use initialPath from Forge history as initialEntries
  const initialEntries = [initialPath]

  return (
    <MemoryRouter initialEntries={initialEntries} initialIndex={0}>
      <HistorySync urlPrefix="" />
      <AppRoutes />
    </MemoryRouter>
  )
}

// Protected route component for admin access
function ProtectedAdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin, isLocal } = useSnapshot(state)

  // Wait for context/isLocal to be loaded before making a decision
  if (isLocal === null) return null

  if (!isLocal && !isAdmin) {
    console.info('ProtectedAdminRoute', 'Access denied - user not admin')
    return (
      <ErrorDisplay
        error={
          new Error(
            'Access denied. You do not have permission to access the admin area. Please contact your administrator.'
          )
        }
      />
    )
  }

  return <>{children}</>
}

// Separate component to handle route detection within the router context
function AppRoutes() {
  const { isAdmin, isLocal, context, availableRadars } = useSnapshot(state)
  const { pathname: currentPath, search: currentSearch } =
    useCurrentPathAndSearch(isLocal ?? false)
  const { rootRoute, routeRadar } = useRadarNav()

  // Update admin access based on current route
  useEffect(() => {
    if (!context) return

    const isAdminModule = context?.moduleKey === 'techradarAdmin'
    const isOnAdminRoute = currentPath?.startsWith('/admin')
    const isAdminAccess = isAdminModule || (isLocal && isOnAdminRoute)

    if (state.isAdmin !== isAdminAccess) {
      console.info('AppRoutes', 'Updating admin access', {
        currentPath,
        isAdminModule,
        isOnAdminRoute,
        oldIsAdmin: state.isAdmin,
        newIsAdmin: isAdminAccess
      })
      state.isAdmin = isAdminAccess || false
    }
  }, [currentPath, context, isLocal])

  // --- BEGIN: Redirect logic and loading state (from ConfigInitializer) ---
  useEffect(() => {
    if (!availableRadars || !context) return

    // Admin context takes precedence
    if (
      context.moduleKey === 'techradarAdmin' &&
      !currentPath?.includes('/admin')
    ) {
      if (availableRadars.length) {
        rootRoute(`radar/${availableRadars[0].id}/admin`)
      } else {
        rootRoute('admin/radar-list')
      }
      return
    }

    // Root path
    const targetRadarPath = `/radar/${availableRadars[0].id}/`
    if (
      currentPath === '/' &&
      availableRadars.length &&
      currentPath !== targetRadarPath
    ) {
      routeRadar(availableRadars[0].id, undefined, currentSearch || undefined)
      return
    }
  }, [
    currentSearch,
    availableRadars,
    context,
    rootRoute,
    routeRadar,
    currentPath
  ])
  // --- END: Redirect logic and loading state ---

  // If not ready, show loading
  if (!availableRadars || !context) {
    return <Loading />
  }

  // If there are no radars, show the admin page button
  if (!availableRadars.length) {
    const adminLink = isLocal
      ? '/admin/radar-list'
      : `${context.siteUrl}/plugins/servlet/ac/ari:cloud:compass:87a7cb09-5333-4c9a-9cc0-ce90374b81a0/techradarAdmin?`

    const handleAdminClick = () => {
      if (isLocal) {
        rootRoute('admin/radar-list')
      } else {
        window.open(adminLink, '_blank')
      }
    }
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-64px)] gap-4">
        <div className="text-lg text-[var(--ds-text)]">
          No radars exist yet.
        </div>
        <button
          type="button"
          onClick={handleAdminClick}
          className="px-4 py-2 rounded bg-[var(--ds-surface-secondary)] text-[var(--ds-text-link)] border border-[var(--ds-border)] hover:bg-[var(--ds-surface-hover)] cursor-pointer"
        >
          Go to Admin Page
        </button>
      </div>
    )
  }

  console.info('AppRoutes', 'Rendering', {
    pathname: currentPath,
    isAdminRoute: currentPath?.startsWith('/admin'),
    isAdmin
  })

  // Check if this is an admin route - only allow if user has admin access
  if (currentPath?.startsWith('/admin')) {
    return (
      <ProtectedAdminRoute>
        <Routes>
          {AdminRouteSet({ prefix: '/admin' })}
          <Route path="*" element={<AdminErrorRoute />} />
        </Routes>
      </ProtectedAdminRoute>
    )
  }
  return <Routes>{MainRouteSet()}</Routes>
}

// Keep AdminApp only for production Forge mode
function AdminApp({ initialPath }: { initialPath?: string }) {
  const { currentRadarId, availableRadars } = useSnapshot(state)
  const [memoryHistory, setMemoryHistory] = useState<{
    listen: (callback: (location: { pathname: string }) => void) => () => void
    push: (path: string) => void
  } | null>(null)

  // Extract the admin sub-path from initialPath
  let adminPath = '/radar-list' // default
  if (initialPath?.startsWith('/admin/')) {
    adminPath = initialPath.replace('/admin', '')
  } else if (initialPath === '/admin') {
    adminPath = '/radar-list'
  }
  const initialEntries = [adminPath]

  console.info('AdminApp', 'Rendering', {
    initialPath,
    adminPath,
    initialEntries,
    currentRadarId,
    availableRadarsReady: !!availableRadars
  })

  // Always call all hooks before any early returns
  useEffect(() => {
    console.info('AdminApp', 'Getting radar data', { currentRadarId })
    if (typeof currentRadarId === 'number') {
      getRadarData(currentRadarId)
    }
  }, [currentRadarId])

  // Sync MemoryRouter with browser history for back button support
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (memoryHistory && event.state?.adminPath) {
        memoryHistory.push(event.state.adminPath)
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [memoryHistory])

  // Wait for radars config to be loaded before rendering routes
  if (availableRadars === null) {
    console.info('AdminApp', 'Waiting for radars config to load')
    return <Loading />
  }

  const handleMemoryRouterRef = (
    router: {
      listen: (callback: (location: { pathname: string }) => void) => () => void
      push: (path: string) => void
    } | null
  ) => {
    if (router && !memoryHistory) {
      setMemoryHistory(router)

      // Listen to memory router changes and update browser history
      const unlisten = router.listen((location: { pathname: string }) => {
        console.info('AdminApp', 'Memory router navigation', {
          newPath: location.pathname
        })
        const adminUrl = `/admin${location.pathname}`
        if (window.location.pathname !== adminUrl) {
          window.history.pushState(
            { adminPath: location.pathname },
            '',
            adminUrl
          )
        }
      })

      return unlisten
    }
  }

  return (
    <MemoryRouter initialEntries={initialEntries} initialIndex={0}>
      <HistorySync onHistoryReady={handleMemoryRouterRef} urlPrefix="/admin" />
      <Routes>
        {AdminRouteSet({})}
        {AdminRouteSet({ prefix: '/admin' })}
        <Route path="*" element={<AdminErrorRoute />} />
      </Routes>
    </MemoryRouter>
  )
}

// Error route component that can access MemoryRouter location
function AdminErrorRoute() {
  const { pathname: currentPath, search: currentSearch } = useLocation()

  logError(
    'AdminErrorRoute',
    `Unmatched route in AdminApp: ${currentPath} and search: ${currentSearch}`,
    new Error(
      `Unable to load Admin page. Current path: ${currentPath}. This usually indicates a routing issue or missing data.`
    )
  )

  return (
    <ErrorDisplay
      error={
        new Error(
          `Unable to load Admin page. Current path: ${currentPath}. This usually indicates a routing issue or missing data.`
        )
      }
    />
  )
}

// Generic HistorySync component for MemoryRouter <-> browser URL sync
function HistorySync({
  onHistoryReady,
  urlPrefix = ''
}: {
  onHistoryReady?: (
    history: {
      listen: (callback: (location: { pathname: string }) => void) => () => void
      push: (path: string) => void
    } | null
  ) => void
  urlPrefix?: string
}): null {
  const navigate = useNavigate()
  const { pathname: currentPath, search: currentSearch } = useLocation()

  useEffect(() => {
    const mockHistory = {
      listen: () => {
        return () => {}
      },
      push: (path: string) => navigate(path)
    }
    if (onHistoryReady) onHistoryReady(mockHistory)
  }, [navigate, onHistoryReady])

  useEffect(() => {
    const targetUrl = `${urlPrefix}${currentPath}${currentSearch}`
    if (window.location.pathname + window.location.search !== targetUrl) {
      window.history.pushState({ path: currentPath }, '', targetUrl)
    }
  }, [currentPath, currentSearch, urlPrefix])

  return null
}
